import { useEffect, useState } from 'react'
import { supabase } from '../../../lib/supabase'
import { useAuth } from '../../../context/AuthContext'
import SuperAdminLayout from '../components/SuperAdminLayout'

export default function SuperAdminSettings() {
  const { user } = useAuth()
  const [settings, setSettings] = useState({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchSettings = async () => {
      const { data } = await supabase.from('settings').select('*')
      if (data) {
        const mapped = {}
        data.forEach(s => mapped[s.key] = s.value)
        setSettings(mapped)
      }
      setLoading(false)
    }
    fetchSettings()
  }, [])

  const handleChange = (key, value) => setSettings(prev => ({ ...prev, [key]: value }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    setSuccess(false)
    const updates = Object.entries(settings).map(([key, value]) =>
      supabase.from('settings').upsert({ key, value, updated_by: user.id, updated_at: new Date() }, { onConflict: 'key' })
    )
    const results = await Promise.all(updates)
    const failed = results.find(r => r.error)
    if (failed) { setError(failed.error.message); setSaving(false); return }
    setSuccess(true)
    setSaving(false)
  }

  const inputClass = "bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 focus:border-green-600 text-gray-900 dark:text-white px-4 py-3 text-sm outline-none transition-colors w-full"
  const labelClass = "text-xs text-gray-500 uppercase tracking-widest font-semibold"

  const daysRemaining = settings.season_end_date
    ? Math.max(0, Math.ceil((new Date(settings.season_end_date) - new Date()) / (1000 * 60 * 60 * 24)))
    : null

  const seasonActive = settings.season_start_date && settings.season_end_date
    ? new Date() >= new Date(settings.season_start_date) && new Date() <= new Date(settings.season_end_date)
    : false

  if (loading) return null

  return (
    <SuperAdminLayout>
      <div className="mb-6 lg:mb-8">
        <h1 className="text-2xl lg:text-4xl font-black uppercase tracking-tight mb-2 text-gray-900 dark:text-white">Platform Settings</h1>
        <p className="text-gray-500 text-sm">Global settings that apply across all clubs on the platform</p>
      </div>

      {success && <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 text-green-600 dark:text-green-400 px-4 py-3 text-sm mb-6">Settings updated successfully</div>}
      {error && <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-red-600 dark:text-red-400 px-4 py-3 text-sm mb-6">{error}</div>}

      <form onSubmit={handleSubmit} className="flex flex-col gap-6 max-w-lg">

        {/* Season Management */}
        <div className="bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 p-5 sm:p-8">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
            <div className="text-xs text-gray-500 uppercase tracking-widest font-semibold">Season Management</div>
            {daysRemaining !== null && (
              <span className={`text-xs font-bold uppercase tracking-widest px-2 py-1 w-fit ${
                seasonActive ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                : daysRemaining === 0 ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400'
              }`}>
                {seasonActive ? `${daysRemaining} days remaining` : daysRemaining === 0 ? 'Season ended' : 'Season not started'}
              </span>
            )}
          </div>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <label className={labelClass}>Season Name</label>
              <input type="text" value={settings.season_name || ''} onChange={(e) => handleChange('season_name', e.target.value)} placeholder="2025/2026 Season" className={inputClass} />
              <p className="text-gray-400 text-xs">This name appears on member dashboards and cards</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <label className={labelClass}>Season Start Date</label>
                <input type="date" value={settings.season_start_date || ''} onChange={(e) => handleChange('season_start_date', e.target.value)} className={inputClass} />
              </div>
              <div className="flex flex-col gap-2">
                <label className={labelClass}>Season End Date</label>
                <input type="date" value={settings.season_end_date || ''} onChange={(e) => handleChange('season_end_date', e.target.value)} className={inputClass} />
              </div>
            </div>
            {settings.season_start_date && settings.season_end_date && (
              <div className="bg-gray-100 dark:bg-black/20 border border-gray-200 dark:border-white/5 px-4 py-3">
                <p className="text-gray-500 dark:text-gray-400 text-xs">
                  All subscriptions paid during this season will be valid until{' '}
                  <span className="text-green-600 dark:text-green-400 font-bold">
                    {new Date(settings.season_end_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </span>
                  {' '}regardless of when payment is made.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Subscription Fees */}
        <div className="bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 p-5 sm:p-8">
          <div className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-6">Subscription Fees</div>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <label className={labelClass}>Platform Subscription Fee (per club/season)</label>
              <div className="flex items-center gap-2">
                <span className="text-gray-500 text-sm px-3 py-3 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 shrink-0">{settings.currency || 'ZMW'}</span>
                <input type="number" value={settings.platform_subscription_fee || ''} onChange={(e) => handleChange('platform_subscription_fee', e.target.value)} placeholder="500" min="0" className={inputClass} />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <label className={labelClass}>Member Subscription Fee (per supporter/season)</label>
              <div className="flex items-center gap-2">
                <span className="text-gray-500 text-sm px-3 py-3 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 shrink-0">{settings.currency || 'ZMW'}</span>
                <input type="number" value={settings.member_subscription_fee || ''} onChange={(e) => handleChange('member_subscription_fee', e.target.value)} placeholder="50" min="0" className={inputClass} />
              </div>
            </div>
          </div>
        </div>

        {/* Currency */}
        <div className="bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 p-5 sm:p-8">
          <div className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-6">Currency</div>
          <div className="flex flex-col gap-2">
            <label className={labelClass}>Currency</label>
            <select value={settings.currency || 'ZMW'} onChange={(e) => handleChange('currency', e.target.value)} className={inputClass}>
              <option value="ZMW">ZMW — Zambian Kwacha</option>
              <option value="GHS">GHS — Ghanaian Cedi</option>
              <option value="NGN">NGN — Nigerian Naira</option>
              <option value="KES">KES — Kenyan Shilling</option>
              <option value="ZAR">ZAR — South African Rand</option>
              <option value="USD">USD — US Dollar</option>
              <option value="GBP">GBP — British Pound</option>
            </select>
          </div>
        </div>

        <button type="submit" disabled={saving} className="bg-green-600 hover:bg-green-700 disabled:bg-green-900 text-white py-3 font-bold uppercase tracking-widest text-sm transition-colors w-fit px-8">
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </form>
    </SuperAdminLayout>
  )
}